import { useEffect, useRef, useState } from 'react';
import { clearAutoplayTimers } from './stateGuards';
import { L } from '../utils/logger';

type Status = 'idle' | 'loading' | 'playing' | 'paused' | 'stopped' | string;

// Minimal surface expected from the mix object. We intentionally type as any to avoid coupling.
type MixLike = {
  currentTime: number;
  status: Status;
};

interface Options {
  // When this key changes (e.g., selected scope), the hook resets its internal state.
  resetKey?: unknown;
  // Max time (ms) to hold the previous time while guarding against a transient regression.
  maxHoldMs?: number;
  // Thresholds to decide if a regression is suspicious.
  nearZeroEpsilon?: number;    // e.g., 0.05s
  regressionTolerance?: number; // e.g., 0.30s
}

/**
 * Stabilizes transport time across pause→play transitions to avoid transient jumps to 0
 * in the UI while audio resumes from the correct position.
 */
export default function useStableTransportTime(mix: MixLike, opts: Options = {}) {
  const {
    resetKey,
    maxHoldMs = 1000,
    nearZeroEpsilon = 0.05,
    regressionTolerance = 0.30,
  } = opts;

  const [stableTime, setStableTime] = useState<number>(0);

  const lastStatusRef = useRef<Status>(mix?.status);
  const lastGoodRef = useRef<number>(0);
  const guardActiveRef = useRef<boolean>(false);
  const guardUntilMsRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  // Reset state when resetKey changes (e.g., new selection/scope)
  useEffect(() => {
    lastGoodRef.current = 0;
    setStableTime(0);
    guardActiveRef.current = false;
    guardUntilMsRef.current = 0;
  }, [resetKey]);

  // Watch status to arm guard on paused→playing
  useEffect(() => {
    const prev = lastStatusRef.current;
    const curr = mix?.status;
    if (prev === 'paused' && curr === 'playing') {
      guardActiveRef.current = true;
      guardUntilMsRef.current = Date.now() + maxHoldMs;
    }
    lastStatusRef.current = curr;
  }, [mix?.status, maxHoldMs]);

  // rAF loop to read time and apply stabilization
  useEffect(() => {
    let mounted = true;

    const loop = () => {
      if (!mounted) return;

      const raw = Number(mix?.currentTime) || 0;
      const status = mix?.status as Status;
      const lastGood = lastGoodRef.current;
      let output = raw;

      const guardActive = guardActiveRef.current;
      const guardExpired = Date.now() > guardUntilMsRef.current;

      if (guardActive && !guardExpired) {
        const looksLikeSpuriousZero = raw <= nearZeroEpsilon && lastGood > regressionTolerance;
        const suspiciousRegression = raw < lastGood - regressionTolerance && lastGood > regressionTolerance;

        if (looksLikeSpuriousZero || suspiciousRegression) {
          // Hold last good value until stream catches up or guard expires.
          output = lastGood;
        } else {
          // Stream caught up; release guard.
          guardActiveRef.current = false;
        }
      } else if (guardActive && guardExpired) {
        // Safety release in case stream didn't catch up in time.
        guardActiveRef.current = false;
      }

      // Update stable time state
      setStableTime(output);

      // Track last good time:
      // - In playing: accept forward movement; ignore unexpected backward jumps (unless guard is active).
      // - In paused/stopped: track whatever we're given (seeks while paused should reflect immediately).
      if (status !== 'playing') {
        lastGoodRef.current = raw;
      } else if (!guardActiveRef.current) {
        if (raw >= lastGood) lastGoodRef.current = raw;
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      mounted = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mix]); // track the same mix object; we poll its properties via rAF

  return stableTime;
}

export function useAutoplayResumeGuard(
  mix: any,
  scopeId: string | number | null | undefined,
  tracks: Array<{ url: string; role?: string }> | undefined
) {
  const autoPlayedScopeRef = useRef<string | number | null>(null);
  const gestureArmedRef = useRef(false);
  const resumeOnceRef = useRef<((e?: any) => void) | null>(null);
  const toRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper: arm one-time user gesture to resume context and play
  const armGestureAutoplay = () => {
    if (gestureArmedRef.current) return;
    gestureArmedRef.current = true;

    const resume = async () => {
      try {
        const an = mix?.getMasterAnalyser?.();
        const ctx = an?.context as AudioContext | undefined;
        if (ctx && ctx.state !== 'running') {
          try { await ctx.resume(); } catch {}
        }
        await mix?.play?.();
      } catch {}
      finally {
        gestureArmedRef.current = false;
        const handler = resumeOnceRef.current;
        if (handler) {
          window.removeEventListener('pointerdown', handler, true);
          window.removeEventListener('keydown', handler, true);
          window.removeEventListener('touchstart', handler, true);
          window.removeEventListener('click', handler, true);
          resumeOnceRef.current = null;
        }
      }
    };

    const handler = () => { void resume(); };
    resumeOnceRef.current = handler;
    window.addEventListener('pointerdown', handler, true);
    window.addEventListener('keydown', handler, true);
    window.addEventListener('touchstart', handler, true);
    window.addEventListener('click', handler, true);
  };

  useEffect(() => {
    const list = tracks || [];
    if (!scopeId || list.length === 0) return;

    if (autoPlayedScopeRef.current === scopeId) return;
    autoPlayedScopeRef.current = scopeId;

    try { clearAutoplayTimers(); } catch {}

    // Kick off autoplay attempt next tick
    toRef.current = setTimeout(() => {
      L.info('media:transport', 'autoplay.try', { scope: String(scopeId), tracks: list.length });
      try { clearAutoplayTimers(); } catch {}

      // Best-effort: if context is suspended, we'll rely on gesture fallback
      try {
        const an0 = mix?.getMasterAnalyser?.();
        const ctx0 = an0?.context as AudioContext | undefined;
        void ctx0; // informational; not strictly needed
      } catch {}

      void mix?.play?.().catch(() => { armGestureAutoplay(); });

      // Check if context actually started; if not, arm gesture
      const check = setTimeout(() => {
        try {
          const an = mix?.getMasterAnalyser?.();
          const running = !!an && (an.context?.state === 'running');
          if (!running) {
            L.warn('media:health', 'autoplay.blocked', { ctxState: an?.context?.state });
            armGestureAutoplay();
          }
        } catch {}
      }, 300);

      const clear = setInterval(() => {
        try {
          const an = mix?.getMasterAnalyser?.();
          if (an && an.context?.state === 'running') {
            L.info('media:transport', 'autoplay.ok');
            clearInterval(clear);
          }
        } catch {}
      }, 500);

      (window as any).__mediaAutoplayTimers ??= [];
      (window as any).__mediaAutoplayTimers.push(check, clear);
    }, 0);

    return () => {
      if (toRef.current) clearTimeout(toRef.current);
      toRef.current = null;
      try { clearAutoplayTimers(); } catch {}
      // Cleanup gesture listeners if still armed
      const handler = resumeOnceRef.current;
      if (handler) {
        window.removeEventListener('pointerdown', handler, true);
        window.removeEventListener('keydown', handler, true);
        window.removeEventListener('touchstart', handler, true);
        window.removeEventListener('click', handler, true);
        resumeOnceRef.current = null;
      }
      gestureArmedRef.current = false;
    };
  }, [scopeId, tracks, mix]);
}
